import { DateTime } from "luxon";
import type { FlightDay, FlightRow } from "./api";
import { dayLabel } from "./format";
import { FROM_COPOPT } from "./days-ahead";

/**
 * The Schedule screen's list: one group per day, headed by the day, how much is on it
 * and, for a day beyond the feed, where its flights were projected from. The range it
 * asks for is capped at what the server will answer.
 */

export const SCHEDULE_MAX_DAYS = 14;

/** The last day a range starting at `from` may show: never before `from`, never past the cap. */
export function rangeEnd(from: string, to: string): string {
  const last = DateTime.fromISO(from).plus({ days: SCHEDULE_MAX_DAYS - 1 }).toISODate()!;
  if (to < from) return from;
  return to > last ? last : to;
}

/** What a day beyond the feed rests on: CopOpt's schedule, or the past weekday it was projected from. */
export function beyondFeed(projectedFrom: string, tz: string): string {
  return projectedFrom === FROM_COPOPT ? "From the CopOpt schedule" : `Projected from ${DateTime.fromISO(projectedFrom, { zone: tz }).toFormat("ccc d LLL")}`;
}

export interface DayGroup {
  date: string;
  /** "Tomorrow · Tue 6 Oct", or just the date further out. */
  heading: string;
  count: string;
  /** Set for a projected day, saying what it rests on. */
  note: string | null;
  rows: FlightRow[];
}

export function groupByDay(days: FlightDay[], today: string, tz: string): DayGroup[] {
  return days.map((d) => {
    const named = dayLabel(d.date, tz, today);
    const full = DateTime.fromISO(d.date, { zone: tz }).toFormat("ccc d LLL");
    const n = d.flights.length;
    return {
      date: d.date,
      heading: named === full ? full : `${named} · ${full}`,
      count: n === 1 ? "1 flight" : `${n} flights`,
      note: d.projectedFrom ? `${beyondFeed(d.projectedFrom, tz)} — the real flights replace these as the feed publishes them` : null,
      rows: [...d.flights].sort((a, b) => a.scheduled - b.scheduled),
    };
  });
}
